import { isEmpty, map } from 'lodash/fp';
import { all, call, getContext, put, takeEvery } from 'redux-saga/effects';
import persistentStorage from 'store2';

import { logout as firebaseLogout } from '@skippay/core/firebase/auth';
import { captureException } from '@skippay/core/global/errorTracking';
import { clearStore as clearUserStore } from '@skippay/core/modules/shared/actions';
import { preventPromiseRejection } from '@skippay/core/utils/promiseUtils';

import { CURRENTLY_LOGGED_USER_EMAIL } from 'constants/authorization';
import { QUERY_PARAM_TOKEN_EXPIRED } from 'constants/queryAndStorageKeys';
import { LOGIN, LOGOUT as LOGOUT_ROUTE } from 'constants/routes';
import { jarabotCustomerContext } from 'jarabot/customerContext/manager';
import { jarabotClient } from 'jarabot/jarabotClient';
import AccountLogoutDeleteMutation from 'mutations/AccountLogoutDeleteMutation.graphql';
import { anonymize } from 'utils/exponeaUtils';

import { SESSION_EXPIRED, USER_LOGOUT } from '../actions';
import { clearUserSessionData } from '../utils/accessTokenUtils';

function* sessionExpiredTask() {
	yield call(clearUserSessionData);
	anonymize();
	jarabotCustomerContext.stop();
	const history = yield getContext('history');
	window.location.assign(
		history.createHref({
			pathname: LOGOUT_ROUTE,
			query: {
				[QUERY_PARAM_TOKEN_EXPIRED]: 1,
				next: history.createHref(history.location),
			},
		})
	);
}

function* userLogoutTask({ payload: { onLogout, variant } }) {
	anonymize();
	jarabotCustomerContext.stop();
	jarabotClient.reset();
	const doOnLogout = map(preventPromiseRejection, onLogout);
	const apolloClient = yield getContext('apolloClient');

	if (!isEmpty(doOnLogout)) {
		yield all(doOnLogout);
	}

	// For mobile app logout is handled by deleteToken endpoint it self
	if (!process.env.MOB_APP_BUILD) {
		yield apolloClient
			// empty object must be send in the body as valid JSON
			.mutate({ mutation: AccountLogoutDeleteMutation, variables: { body: {} } })
			.catch((err) => {
				captureException(new Error(`Call of 'logout' failed`), {
					extra: {
						originalError: err,
					},
				});
			});
	}

	yield call(clearUserSessionData);
	localStorage.removeItem(CURRENTLY_LOGGED_USER_EMAIL);
	yield put(clearUserStore());

	yield firebaseLogout();

	if (variant === 'TECHNICAL') {
		persistentStorage.set('introScreenViewed', 1);

		window.location.assign(`${LOGIN}?error=TECHNICAL`);

		return;
	}

	window.location.assign(LOGOUT_ROUTE);
}

export default function* watchLogout() {
	yield all([
		takeEvery(USER_LOGOUT, userLogoutTask),
		takeEvery(SESSION_EXPIRED, sessionExpiredTask),
	]);
}
